import React, { useState, useEffect } from 'react';
import { 
  getChecklistsByProject, 
  getChecklistItems, 
  createChecklist, 
  createChecklistItem, 
  updateChecklistItem, 
  deleteChecklistItem, 
  toggleChecklistItem,
  reorderChecklistItems,
  deleteChecklist
} from '../services/checklistService';
import { Checklist, ChecklistItem } from '../types';
import { Plus, X, Trash2, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';

interface ChecklistComponentProps {
  projectId: string;
}

const ChecklistComponent: React.FC<ChecklistComponentProps> = ({ projectId }) => {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [expandedChecklists, setExpandedChecklists] = useState<Record<string, boolean>>({});
  const [checklistItems, setChecklistItems] = useState<Record<string, ChecklistItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newChecklistName, setNewChecklistName] = useState('');
  const [newItemTexts, setNewItemTexts] = useState<Record<string, string>>({});
  const [deletingChecklist, setDeletingChecklist] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch checklists for the project
  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        setLoading(true);
        const fetchedChecklists = await getChecklistsByProject(projectId);
        setChecklists(fetchedChecklists);
        
        // Initialize expanded state for all checklists
        const initialExpandedState: Record<string, boolean> = {};
        fetchedChecklists.forEach(checklist => {
          initialExpandedState[checklist.id] = true; // Default to expanded
        });
        setExpandedChecklists(initialExpandedState);
        
        // Initialize new item text state for all checklists
        const initialNewItemTexts: Record<string, string> = {};
        fetchedChecklists.forEach(checklist => {
          initialNewItemTexts[checklist.id] = '';
        });
        setNewItemTexts(initialNewItemTexts);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching checklists:', err);
        setError('Kunde inte hämta checklistor. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChecklists();
  }, [projectId]);

  // Fetch checklist items when a checklist is expanded
  useEffect(() => {
    const fetchChecklistItems = async () => {
      try {
        const expandedChecklistIds = Object.entries(expandedChecklists)
          .filter(([_, isExpanded]) => isExpanded)
          .map(([id]) => id);
        
        for (const checklistId of expandedChecklistIds) {
          if (!checklistItems[checklistId]) {
            const items = await getChecklistItems(checklistId);
            setChecklistItems(prev => ({
              ...prev,
              [checklistId]: items
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching checklist items:', err);
        setError('Kunde inte hämta checklistobjekt. Försök igen senare.');
      }
    };
    
    fetchChecklistItems();
  }, [expandedChecklists, checklistItems]);

  // Toggle checklist expansion
  const toggleChecklist = (checklistId: string) => {
    setExpandedChecklists(prev => ({
      ...prev,
      [checklistId]: !prev[checklistId]
    }));
  };

  // Create a new checklist
  const handleCreateChecklist = async () => {
    if (!newChecklistName.trim()) return;
    
    try {
      const newChecklist = await createChecklist(
        projectId,
        'custom', // Default type
        newChecklistName.trim()
      );
      
      setChecklists(prev => [...prev, newChecklist]);
      setExpandedChecklists(prev => ({
        ...prev,
        [newChecklist.id]: true
      }));
      setNewItemTexts(prev => ({
        ...prev,
        [newChecklist.id]: ''
      }));
      setNewChecklistName('');
    } catch (err) {
      console.error('Error creating checklist:', err);
      setError('Kunde inte skapa checklista. Försök igen senare.');
    }
  };

  // Create a new checklist item
  const handleCreateChecklistItem = async (checklistId: string) => {
    const text = newItemTexts[checklistId]?.trim();
    if (!text) return;
    
    try {
      const newItem = await createChecklistItem(checklistId, text);
      
      setChecklistItems(prev => ({
        ...prev,
        [checklistId]: [...(prev[checklistId] || []), newItem]
      }));
      setNewItemTexts(prev => ({
        ...prev,
        [checklistId]: ''
      }));
    } catch (err) {
      console.error('Error creating checklist item:', err);
      setError('Kunde inte skapa checklistobjekt. Försök igen senare.');
    }
  };

  // Toggle a checklist item's completed status
  const handleToggleChecklistItem = async (itemId: string, checklistId: string, completed: boolean) => {
    try {
      await toggleChecklistItem(itemId, !completed);
      
      setChecklistItems(prev => ({
        ...prev,
        [checklistId]: prev[checklistId].map(item => 
          item.id === itemId ? { ...item, completed: !completed } : item
        )
      }));
    } catch (err) {
      console.error('Error toggling checklist item:', err);
      setError('Kunde inte uppdatera checklistobjekt. Försök igen senare.');
    }
  };

  // Delete a checklist item
  const handleDeleteChecklistItem = async (itemId: string, checklistId: string) => {
    try {
      await deleteChecklistItem(itemId);
      
      setChecklistItems(prev => ({
        ...prev,
        [checklistId]: prev[checklistId].filter(item => item.id !== itemId)
      }));
    } catch (err) {
      console.error('Error deleting checklist item:', err);
      setError('Kunde inte ta bort checklistobjekt. Försök igen senare.');
    }
  };

  // Move an item up or down within its checklist
  const handleMoveItem = async (itemId: string, checklistId: string, direction: 'up' | 'down') => {
    const items = [...(checklistItems[checklistId] || [])];
    const currentIndex = items.findIndex(item => item.id === itemId);
    
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === items.length - 1) return;
    
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [items[currentIndex], items[targetIndex]] = [items[targetIndex], items[currentIndex]];
    
    const updatedItems = items.map((item, index) => ({ ...item, order: index }));
    
    setChecklistItems(prev => ({ ...prev, [checklistId]: updatedItems }));
    
    try {
      await reorderChecklistItems(updatedItems.map(item => ({ id: item.id, order: item.order })));
    } catch (err) {
      console.error('Error reordering checklist items:', err);
      setError('Kunde inte ändra ordning. Försök igen.');
    }
  };

  // Delete a checklist - show confirmation dialog
  const handleDeleteChecklist = (checklistId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent toggling the checklist when clicking delete
    setDeleteConfirmId(checklistId);
  };

  const confirmDeleteChecklist = async () => {
    if (!deleteConfirmId) return;

    try {
      setDeletingChecklist(deleteConfirmId);
      await deleteChecklist(deleteConfirmId);

      // Update the state to remove the deleted checklist
      setChecklists(prev => prev.filter(checklist => checklist.id !== deleteConfirmId));

      // Clean up related state
      setExpandedChecklists(prev => {
        const newState = { ...prev };
        delete newState[deleteConfirmId];
        return newState;
      });

      setChecklistItems(prev => {
        const newState = { ...prev };
        delete newState[deleteConfirmId];
        return newState;
      });

      setNewItemTexts(prev => {
        const newState = { ...prev };
        delete newState[deleteConfirmId];
        return newState;
      });

      setError(null);
    } catch (err) {
      console.error('Error deleting checklist:', err);
      setError('Kunde inte ta bort checklistan. Försök igen senare.');
    } finally {
      setDeletingChecklist(null);
      setDeleteConfirmId(null);
    }
  };

  if (loading && checklists.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog
        isOpen={deleteConfirmId !== null}
        title="Ta bort"
        message="Är du säker på att du vill ta bort denna checklista? Alla objekt i checklistan kommer också att tas bort."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={deletingChecklist === deleteConfirmId}
        onConfirm={confirmDeleteChecklist}
        onCancel={() => setDeleteConfirmId(null)}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl">
          <p className="font-medium">{error}</p>
        </div>
      )}
      
      {/* Create New Checklist */}
      <div className="bg-surface rounded-xl shadow-sm p-4">
        <h2 className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
          <ClipboardList size={20} className="text-primary-500" />
          <span>Skapa ny checklista</span>
        </h2>
        
        <div className="flex gap-3">
          <input
            type="text"
            value={newChecklistName}
            onChange={(e) => setNewChecklistName(e.target.value)}
            placeholder="Namn på checklista..."
            className="flex-1 px-4 py-3 border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface-alt"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateChecklist();
              }
            }}
          />
          <button
            onClick={handleCreateChecklist}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!newChecklistName.trim()}
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Skapa</span>
          </button>
        </div>
      </div>
      
      {checklists.length === 0 && !loading ? (
        <div className="bg-surface rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-surface-alt rounded-full flex items-center justify-center mx-auto mb-4">
            <ClipboardList size={32} className="text-on-surface-tertiary" />
          </div>
          <h3 className="text-lg font-medium text-on-surface mb-2">Inga checklistor än</h3>
          <p className="text-on-surface-tertiary">Skapa din första checklista för att komma igång</p>
        </div>
      ) : (
        <div className="space-y-4">
          {checklists.map((checklist) => {
            const items = checklistItems[checklist.id] || [];
            const completedItems = items.filter(item => item.completed).length;
            const totalItems = items.length;
            const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
            
            return (
              <div key={checklist.id} className="bg-surface rounded-xl shadow-sm overflow-hidden">
                {/* Checklist Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-surface-hover transition-colors"
                  onClick={() => toggleChecklist(checklist.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <ClipboardList size={20} className="text-primary-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-on-surface text-lg truncate">{checklist.name}</h3>
                          {totalItems > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 bg-surface-alt rounded-full h-2 max-w-32">
                                <div 
                                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${progressPercentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-on-surface-tertiary font-medium">
                                {completedItems}/{totalItems}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleDeleteChecklist(checklist.id, e)}
                        className="p-2 text-on-surface-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        disabled={deletingChecklist === checklist.id}
                        aria-label="Ta bort checklista"
                      >
                        {deletingChecklist === checklist.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                      
                      <ChevronDown 
                        size={20} 
                        className={`text-on-surface-tertiary transition-transform ${expandedChecklists[checklist.id] ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Checklist Items */}
                {expandedChecklists[checklist.id] && (
                  <div className="border-t border-theme-border-subtle p-4">
                    <div className="space-y-3 mb-4">
                      {items.length > 0 ? (
                        items.map((item, index) => (
                          <div 
                            key={item.id}
                            className="flex items-center gap-2 p-3 bg-surface-alt rounded-lg"
                          >
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleMoveItem(item.id, checklist.id, 'up')}
                                disabled={index === 0}
                                className="-m-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-on-surface-tertiary disabled:opacity-20"
                                aria-label="Flytta upp"
                              >
                                <ChevronUp size={16} />
                              </button>
                              <button
                                onClick={() => handleMoveItem(item.id, checklist.id, 'down')}
                                disabled={index === items.length - 1}
                                className="-m-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-on-surface-tertiary disabled:opacity-20"
                                aria-label="Flytta ner"
                              >
                                <ChevronDown size={16} />
                              </button>
                            </div>
                            
                            <button
                              onClick={() => handleToggleChecklistItem(item.id, checklist.id, item.completed)}
                              className={`w-7 h-7 min-w-[28px] rounded-md border-2 flex items-center justify-center transition-colors ${
                                item.completed 
                                  ? 'bg-primary-600 border-primary-600 text-white' 
                                  : 'border-theme-border active:border-primary-300'
                              }`}
                            >
                              {item.completed && <span className="text-sm">✓</span>}
                            </button>
                            
                            <span className={`flex-1 min-w-0 ${item.completed ? 'line-through text-on-surface-tertiary' : 'text-on-surface'}`}>
                              {item.text}
                            </span>
                            
                            <button
                              onClick={() => handleDeleteChecklistItem(item.id, checklist.id)}
                              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-on-surface-tertiary active:text-red-500 active:bg-red-50 dark:active:bg-red-900/20 rounded-lg transition-colors"
                              aria-label="Ta bort objekt"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-on-surface-tertiary">
                          <ClipboardList size={32} className="mx-auto mb-2 text-on-surface-tertiary" />
                          <p>Inga objekt i denna checklista än</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Add New Item */}
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={newItemTexts[checklist.id] || ''}
                        onChange={(e) => setNewItemTexts(prev => ({
                          ...prev,
                          [checklist.id]: e.target.value
                        }))}
                        placeholder="Lägg till objekt..."
                        className="flex-1 px-4 py-3 border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateChecklistItem(checklist.id);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleCreateChecklistItem(checklist.id)}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!newItemTexts[checklist.id]?.trim()}
                      >
                        <Plus size={18} />
                        <span className="hidden sm:inline">Lägg till</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChecklistComponent; 